import type { CriterioRateioDto, RateioFichaResponse } from '@triade/contracts';
import { formatarCusto, formatarFracaoPercentual, formatarMoeda, formatarNumeroCompleto, formatarQuantidade } from '@/lib/format';
import { rotuloUnidade } from '@/lib/i18n-pt';

const ROTULO_CRITERIO: Record<CriterioRateioDto, string> = { VALOR_VENDA: 'Valor de venda relativo', QUANTIDADE: 'Quantidade física' };

/**
 * Tabela de rateio do custo conjunto (Sessao 31): uma linha por saida principal (a da ficha e os coprodutos). So exibe o que a API
 * devolveu; nada de somar ou dividir aqui. Sem `comCusto` (custo.ver) ou sem `rateio` (sem coproduto) nao renderiza nada:
 * nenhum R$ e exibido para quem nao ve custo, mesmo que a API mande o rateio.
 */
export function RateioFicha({ rateio, comCusto }: { rateio: RateioFichaResponse | undefined; comCusto: boolean }) {
  if (!comCusto || !rateio) return null;
  return (
    <section aria-label="Rateio do custo conjunto" data-testid="rateio-ficha" className="rounded-2xl border border-border bg-card">
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-title font-semibold">Rateio do custo conjunto</h2>
        <div className="text-small text-ink-muted">Critério aplicado: {ROTULO_CRITERIO[rateio.criterioAplicado]}</div>
      </div>

      {rateio.fallback && rateio.aviso && (
        <p role="status" className="border-b border-border bg-warning-bg px-5 py-2 text-small text-warning-ink">
          {rateio.aviso.message}
        </p>
      )}

      <div className="overflow-x-auto px-5 py-3">
        <table className="w-full min-w-[640px] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="py-2 pr-3 font-medium">Saída</th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">Quantidade</th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">Valor de venda (R$)</th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">Base</th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">%</th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">Custo (R$)</th>
              <th scope="col" className="py-2 text-right font-medium">Custo por unidade (R$)</th>
            </tr>
          </thead>
          <tbody>
            {rateio.linhas.map((l) => (
              <tr key={l.itemId} className="border-t border-line-soft" data-testid="rateio-linha">
                <td className="py-2 pr-3">
                  <span className="flex items-center gap-2">
                    <span className="rounded bg-line-soft px-1.5 text-caption font-medium">{l.itemCodigo}</span>
                    {l.principal && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-caption font-semibold text-primary">Principal</span>}
                  </span>
                </td>
                <td className="tabular py-2 pr-3 text-right">
                  {formatarQuantidade(l.quantidade)} {l.unidade ? rotuloUnidade(l.unidade) : ''}
                </td>
                <td className="tabular py-2 pr-3 text-right">{formatarMoeda(l.valorVenda)}</td>
                <td className="tabular py-2 pr-3 text-right">{formatarNumeroCompleto(l.base)}</td>
                <td className="tabular py-2 pr-3 text-right">{formatarFracaoPercentual(l.percentual)}</td>
                <td className="tabular py-2 pr-3 text-right">{formatarMoeda(l.custo)}</td>
                <td className="tabular py-2 text-right">{formatarCusto(l.custoUnitario)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border font-semibold">
              <th scope="row" colSpan={5} className="py-2 pr-3 text-left font-semibold">
                Custo conjunto líquido
              </th>
              <td className="tabular py-2 pr-3 text-right" data-testid="rateio-conjunto">
                {formatarMoeda(rateio.conjunto)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
